/**
 * WebTour — first-use coach marks (max 5 steps) for the discovery home.
 * Spotlights real elements via data-testid, skippable, persists to
 * localStorage (guests) + server flag tour_home_done (users).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { patchTours } from "../../api/tourApi";

export const TOUR_LS_KEY = "da_tour_home_v1";

const STEPS = [
  { target: "home-cta-auto", key: "what" },
  { target: "home-top-deals", key: "signal" },
  { target: "home-cta-immo", key: "save" },
];

function rectFor(testid) {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { x: r.left, y: r.top, w: r.width, h: r.height };
}

export function shouldShowHomeTour(user) {
  if (typeof window === "undefined") return false;
  if (window.localStorage.getItem(TOUR_LS_KEY) === "done") return false;
  if (user && user.tour_home_done) return false;
  return true;
}

export default function WebTour({ onClose }) {
  const { t } = useTranslation("discover");
  const { user } = useAuth();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  const step = STEPS[idx];

  useEffect(() => {
    const measure = () => setRect(rectFor(step.target));
    measure();
    const id = setTimeout(measure, 120);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(id);
      window.removeEventListener("resize", measure);
    };
  }, [step]);

  const finish = useCallback(() => {
    window.localStorage.setItem(TOUR_LS_KEY, "done");
    if (user) patchTours({ tour_home_done: true }).catch(() => {});
    onClose();
  }, [user, onClose]);

  const last = idx === STEPS.length - 1;
  const pad = 8;
  const hole = rect
    ? {
        left: Math.max(rect.x - pad, 4),
        top: Math.max(rect.y - pad, 4),
        width: rect.w + pad * 2,
        height: rect.h + pad * 2,
      }
    : null;
  const below = hole ? hole.top + hole.height + 200 < window.innerHeight : false;

  return (
    <div className="fixed inset-0 z-[80]" data-testid="home-tour" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-black/70"
        style={
          hole
            ? {
                clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 ${hole.top}px, ${hole.left}px ${hole.top}px, ${hole.left}px ${hole.top + hole.height}px, ${hole.left + hole.width}px ${hole.top + hole.height}px, ${hole.left + hole.width}px ${hole.top}px, 0 ${hole.top}px)`,
              }
            : undefined
        }
      />
      {hole && (
        <div
          className="pointer-events-none absolute rounded-xl border-2 border-emerald-400/80"
          style={{ left: hole.left, top: hole.top, width: hole.width, height: hole.height }}
        />
      )}
      <div
        className="absolute left-1/2 w-[min(92vw,380px)] -translate-x-1/2 rounded-2xl border border-zinc-700 bg-zinc-900 p-4 shadow-2xl shadow-black/60"
        style={
          hole
            ? below
              ? { top: hole.top + hole.height + 14 }
              : { top: Math.max(hole.top - 190, 14) }
            : { top: "35%" }
        }
      >
        <div className="mb-1 flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <span
              key={s.key}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-5 bg-emerald-400" : "w-1.5 bg-zinc-700"
              }`}
            />
          ))}
        </div>
        <div className="text-sm font-bold text-zinc-50">{t(`tour.${step.key}Title`)}</div>
        <p className="mt-1 text-[13px] leading-relaxed text-zinc-400">{t(`tour.${step.key}Body`)}</p>
        <div className="mt-3 flex items-center justify-between">
          <button
            type="button"
            data-testid="home-tour-skip"
            onClick={finish}
            className="text-xs text-zinc-500 hover:text-zinc-300"
          >
            {t("tour.skip")}
          </button>
          <button
            type="button"
            data-testid="home-tour-next"
            onClick={() => (last ? finish() : setIdx((i) => i + 1))}
            className="rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-bold text-zinc-950 hover:bg-emerald-400"
          >
            {last ? t("tour.done") : t("tour.next")}
          </button>
        </div>
      </div>
    </div>
  );
}
